import { useEffect, useState } from "react";
import {
  adminListPosts, adminCreatePost, adminUpdatePost, adminDeletePost, adminUpload,
  resolveImage, formatApiErrorDetail,
} from "@/lib/adminApi";
import RichEditor from "@/components/RichEditor";

const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = {
  title: "", date: today(), author: "", body_html: "", excerpt: "",
  meta_title: "", meta_description: "", image_url: "", tags: "", published: true,
};

export default function BlogPanel({ authorDefault = "" }) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    try { setRows(await adminListPosts()); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => setEditing({ ...EMPTY, date: today(), author: authorDefault });
  const openEdit = (p) => setEditing({ ...EMPTY, ...p, tags: (p.tags || []).join(", ") });
  const set = (k, v) => setEditing((s) => ({ ...s, [k]: v }));

  const uploadCover = async (file) => {
    setBusy(true);
    try { const { url } = await adminUpload(file); set("image_url", url); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try { await adminDeletePost(p.id); await load(); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const save = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const body = {
        title: editing.title, date: editing.date, author: editing.author,
        body_html: editing.body_html, excerpt: editing.excerpt,
        meta_title: editing.meta_title, meta_description: editing.meta_description,
        image_url: editing.image_url, published: !!editing.published,
        tags: editing.tags.split(",").map((t) => t.trim()).filter(Boolean),
      };
      if (editing.id) await adminUpdatePost(editing.id, body);
      else await adminCreatePost(body);
      setEditing(null);
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <section className="admin-subs" data-testid="blog-panel">
      <div className="admin-subs__top">
        <p className="mono-caps admin-hint" data-testid="blog-count">{rows.length} post{rows.length === 1 ? "" : "s"}</p>
        <button className="admin-btn mono-caps" onClick={openCreate} data-testid="blog-new">+ New post</button>
      </div>
      {error && <p className="admin-error" data-testid="blog-error">{error}</p>}

      {rows.length === 0 ? (
        <p className="admin-subs__empty" data-testid="blog-empty">No posts yet — write your first one with “+ New post”.</p>
      ) : (
        <ul className="admin-posts">
          {rows.map((p) => (
            <li key={p.id} className="admin-post" data-testid={`admin-post-${p.slug}`}>
              <div className="admin-post__thumb">{p.image_url && <img src={resolveImage(p.image_url)} alt="" loading="lazy" />}</div>
              <div className="admin-post__body">
                <p className="mono-caps admin-post__meta">
                  {p.date}{p.author ? ` · ${p.author}` : ""} ·{" "}
                  <span className={p.published ? "admin-post__state is-live" : "admin-post__state"} data-testid={`admin-post-state-${p.slug}`}>
                    {p.published ? "Published" : "Draft"}
                  </span>
                </p>
                <h3 className="admin-card__title">{p.title}</h3>
                <p className="mono-caps admin-card__slug">/blog/{p.slug}{p.tags?.length ? ` · ${p.tags.join(", ")}` : ""}</p>
              </div>
              <div className="admin-card__actions">
                <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="admin-mini mono-caps">View</a>
                <button type="button" onClick={() => openEdit(p)} className="admin-mini mono-caps" data-testid={`admin-post-edit-${p.slug}`}>Edit</button>
                <button type="button" onClick={() => remove(p)} className="admin-mini admin-mini--danger mono-caps" data-testid={`admin-post-delete-${p.slug}`}>Delete</button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <div className="admin-modal" role="dialog" aria-modal="true" data-testid="blog-modal">
          <button className="admin-modal__scrim" onClick={() => setEditing(null)} aria-label="Close" />
          <form className="admin-modal__panel admin-modal__panel--wide" onSubmit={save}>
            <div className="admin-modal__top">
              <h2>{editing.id ? "Edit post" : "New post"}</h2>
              <button type="button" onClick={() => setEditing(null)} className="admin-mini mono-caps" data-testid="blog-modal-close">Close</button>
            </div>

            <div className="admin-modal__body admin-modal__body--post">
              <div className="admin-modal__images">
                <label className="admin-field"><span className="mono-caps">Blog title</span>
                  <input value={editing.title} onChange={(e) => set("title", e.target.value)} required data-testid="blog-field-title" />
                </label>
                <div className="admin-row">
                  <label className="admin-field"><span className="mono-caps">Date</span>
                    <input type="date" value={editing.date} onChange={(e) => set("date", e.target.value)} required data-testid="blog-field-date" />
                  </label>
                  <label className="admin-field"><span className="mono-caps">Author</span>
                    <input value={editing.author} onChange={(e) => set("author", e.target.value)} placeholder="e.g. Warren Wulkan" data-testid="blog-field-author" />
                  </label>
                </div>
                <label className="admin-field">
                  <span className="mono-caps">Blog image</span>
                  {editing.image_url ? (
                    <div className="admin-preview">
                      <img src={resolveImage(editing.image_url)} alt="" />
                      <button type="button" className="admin-mini" onClick={() => set("image_url", "")}>Remove</button>
                    </div>
                  ) : (
                    <input type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && uploadCover(e.target.files[0])} data-testid="blog-upload-image" />
                  )}
                </label>
                <label className="admin-field"><span className="mono-caps">Tags (comma separated)</span>
                  <input value={editing.tags} onChange={(e) => set("tags", e.target.value)} placeholder="studio, process, exhibitions" data-testid="blog-field-tags" />
                </label>
                <label className="admin-field"><span className="mono-caps">Short excerpt (shown in the listing)</span>
                  <textarea rows={3} value={editing.excerpt} onChange={(e) => set("excerpt", e.target.value)} maxLength={400} placeholder="One or two lines. Left blank, the opening of the post is used." data-testid="blog-field-excerpt" />
                </label>
                <label className="admin-field"><span className="mono-caps">Meta title (search engines)</span>
                  <input value={editing.meta_title} onChange={(e) => set("meta_title", e.target.value)} maxLength={160} placeholder="Defaults to the blog title" data-testid="blog-field-meta-title" />
                </label>
                <label className="admin-field"><span className="mono-caps">Meta description</span>
                  <textarea rows={3} value={editing.meta_description} onChange={(e) => set("meta_description", e.target.value)} maxLength={320} placeholder="Up to ~160 characters for best results" data-testid="blog-field-meta-description" />
                </label>
                <label className="admin-check">
                  <input type="checkbox" checked={!!editing.published} onChange={(e) => set("published", e.target.checked)} data-testid="blog-field-published" />
                  <span className="mono-caps">{editing.published ? "Published — visible to visitors" : "Draft — hidden from visitors"}</span>
                </label>
              </div>

              <div className="admin-modal__fields">
                <span className="mono-caps admin-field-label">Post</span>
                <RichEditor value={editing.body_html} onChange={(v) => set("body_html", v)} onError={setError} />
              </div>
            </div>

            <div className="admin-modal__actions">
              <button type="submit" className="admin-btn mono-caps" disabled={busy} data-testid="blog-save">
                {busy ? "Saving…" : editing.id ? "Save changes" : "Create post"}
              </button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}
